import { MNSproCloudApi } from '..';
import {
  GroupTemplateCreateRequest,
  GroupTemplateResponse,
  GroupTemplateUpdateRequest,
  UserGroupResponse,
  UserTemplateAvailableLicenseResponse,
  UserTemplateCreateRequest,
  UserTemplatePermissionRoleResponse,
  UserTemplateResponse,
  UserTemplateUpdateRequest,
} from '../api/v2';

export class TemplatesService {
  private static getApi(authToken: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    return MNSproCloudApi.V2.TemplatesApiFactory(config);
  }

  static async getAllUserTemplates(authToken: string, organizationId: string) {
    const api = TemplatesService.getApi(authToken);
    const response = await api.getAll(organizationId);

    return response.data;
  }

  static async getAllDomains(
    authToken: string,
    organizationId: string,
  ): Promise<string[]> {
    const api = TemplatesService.getApi(authToken);
    const response = await api.getAllDomains(organizationId);

    return response.data;
  }

  static async getAllGroups(
    authToken: string,
    organizationId: string,
  ): Promise<UserGroupResponse[]> {
    const api = TemplatesService.getApi(authToken);
    const response = await api.getAllGroups(organizationId);

    return response.data;
  }

  static async getAllPermissionRoles(
    authToken: string,
    organizationId: string,
  ): Promise<UserTemplatePermissionRoleResponse[]> {
    const api = TemplatesService.getApi(authToken);
    const response = await api.getAllPermissions(organizationId);

    return response.data;
  }

  static async getAllLicenses(
    authToken: string,
    organizationId: string,
  ): Promise<UserTemplateAvailableLicenseResponse[]> {
    const api = TemplatesService.getApi(authToken);
    const response = await api.getAvailableLicenses(organizationId);

    return response.data;
  }

  static async destroyUserTemplate(
    authToken: string,
    organizationId: string,
    templateId: string,
  ) {
    const api = TemplatesService.getApi(authToken);
    await api._delete(organizationId, templateId);
  }

  static async updateUserTemplate(
    authToken: string,
    organizationId: string,
    userTemplateUpdateRequest: UserTemplateUpdateRequest,
  ) {
    const api = TemplatesService.getApi(authToken);
    const response = await api.update(
      organizationId,
      userTemplateUpdateRequest,
    );

    return response.data;
  }

  static async createUserTemplate(
    authToken: string,
    organizationId: string,
    userTemplateCreateRequest: UserTemplateCreateRequest,
  ): Promise<UserTemplateResponse> {
    const api = TemplatesService.getApi(authToken);
    const response = await api.create(
      organizationId,
      userTemplateCreateRequest,
    );

    return response.data;
  }

  static async getAllGroupTemplates(
    authToken: string,
    organizationId: string,
  ): Promise<GroupTemplateResponse[]> {
    const api = TemplatesService.getApi(authToken);
    const response = await api.getAllGroupTemplates(organizationId);

    return response.data;
  }

  static async getGroupTemplate(
    authToken: string,
    organizationId: string,
    templateId: string,
  ): Promise<GroupTemplateResponse> {
    const api = TemplatesService.getApi(authToken);
    const response = await api.getGroupTemplate(organizationId, templateId);

    return response.data;
  }

  static async updateGroupTemplate(
    authToken: string,
    organizationId: string,
    groupTemplateUpdateRequest: GroupTemplateUpdateRequest,
  ): Promise<GroupTemplateResponse> {
    const api = TemplatesService.getApi(authToken);
    const response = await api.updateGroupTemplate(
      organizationId,
      groupTemplateUpdateRequest,
    );

    return response.data;
  }

  static async createGroupTemplate(
    authToken: string,
    organizationId: string,
    groupTemplateCreateRequest: GroupTemplateCreateRequest,
  ): Promise<GroupTemplateResponse> {
    const api = TemplatesService.getApi(authToken);
    const response = await api.createGroupTemplate(
      organizationId,
      groupTemplateCreateRequest,
    );

    return response.data;
  }

  static async destroyGroupTemplate(
    authToken: string,
    organizationId: string,
    templateId: string,
  ): Promise<void> {
    const api = TemplatesService.getApi(authToken);
    await api.deleteGroupTemplate(organizationId, templateId);
  }
}
